import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { Calculator, Calendar, Book, GraduationCap, Award, FileText, ClipboardList } from "lucide-react";
import axios from "axios";

const CoursePage = () => {
    const { courseid } = useParams();
    const [course, setCourse] = useState(null);
    const [overallProgress, setOverallProgress] = useState(0);
    const [flippedCard, setFlippedCard] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Utility function to calculate average
    const average = (arr) => {
        const validNumbers = arr
            .map(val => parseFloat(val.toString()))
            .filter(val => !isNaN(val));
        return validNumbers.length > 0
            ? validNumbers.reduce((a, b) => a + b, 0) / validNumbers.length
            : 0;
    };

    // Fetch course data from server
    useEffect(() => {
        const fetchCourseData = async () => {
            try {
                axios.defaults.withCredentials = true;
                const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/course/get-all-course-data-student`, { courseID: courseid });
                const result = await response.data;

                if (result.message === "Success") {
                    setCourse(result.data);

                    // Calculate overall progress
                    const quizMarks = result.data.quizzes.map(quiz =>
                        parseFloat(quiz.marks.toString()) || 0
                    );
                    const assignmentMarks = result.data.assignments.map(assignment =>
                        parseFloat(assignment.marks.toString()) || 0
                    );
                    const examMarks = result.data.exams.map(exam =>
                        parseFloat(exam.marks.toString()) || 0
                    );

                    const combinedMarks = [...quizMarks, ...assignmentMarks, ...examMarks];
                    const overallAvg = combinedMarks.length > 0
                        ? (average(combinedMarks) / 10) * 100
                        : 0;

                    setOverallProgress(Math.round(overallAvg));
                }

                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching course data:", error);
                setIsLoading(false);
            }
        };

        if (courseid) {
            fetchCourseData();
        }
    }, [courseid]);

    // Generate performance data for charts
    const getPerformanceData = (type) => {
        if (!course) return [];

        return course[type].map((item, index) => ({
            name: item.name || `${type.charAt(0).toUpperCase() + type.slice(1)} ${index + 1}`,
            value: parseFloat(item.marks.toString()) || 0,
        }));
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    // No course data found
    if (!course) {
        return (
            <div className="flex items-center justify-center h-screen text-xl text-gray-600">
                No course data found
            </div>
        );
    }

    // Reusable performance card component
    const PerformanceCard = ({ type, icon: Icon, color }) => {
        const items = course[type];
        const performanceData = getPerformanceData(type);

        return (
            <div
                className={`relative h-[300px] cursor-pointer perspective-1000`}
                onClick={() =>
                    setFlippedCard(flippedCard === type ? null : type)
                }
            >
                <div
                    className={`relative w-full h-full transition-transform duration-500 transform-style-3d ${flippedCard === type ? "rotate-y-180" : ""
                        }`}
                >
                    {/* Front of Card */}
                    <div className={`absolute w-full h-full bg-${color}-50 rounded-lg p-4 backface-hidden flex flex-col`}>
                        <h3 className={`text-lg font-semibold text-${color}-700 mb-4 flex items-center gap-2`}>
                            <Icon className="w-5 h-5" />
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                        </h3>
                        <div className="flex-grow">
                            {items.map((item, index) => (
                                <div
                                    key={`${type}-${index}`}
                                    className="flex justify-between items-center mb-3"
                                >
                                    <span className="text-gray-600">
                                        {item.name || `${type.charAt(0).toUpperCase() + type.slice(1)} ${index + 1}`}
                                    </span>
                                    <span className="font-medium">
                                        {item.marks || 0}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div className="mt-auto pt-4 border-t border-blue-200 text-center">
                            <div className="text-sm text-gray-600 mb-1">
                                Average Score
                            </div>
                            <div className={`text-2xl font-bold text-${color}-700`}>
                                {(average(
                                    items.map(item => item.marks)
                                )).toFixed(1)}
                            </div>
                        </div>
                    </div>

                    {/* Back of Card */}
                    <div className={`absolute w-full h-full bg-${color}-50 rounded-lg p-4 rotate-y-180 backface-hidden`}>
                        <h3 className={`text-lg font-semibold text-${color}-700 mb-4`}>
                            {type.charAt(0).toUpperCase() + type.slice(1)} Performance Trend
                        </h3>
                        <div className="h-[200px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={performanceData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                                    <YAxis />
                                    <Tooltip />
                                    <Line
                                        type="monotone"
                                        dataKey="value"
                                        stroke={color === 'blue' ? "#2563eb" : color === 'green' ? "#10b981" : "#f43f5e"}
                                        strokeWidth={2}
                                        dot={{ fill: color === 'blue' ? "#2563eb" : color === 'green' ? "#10b981" : "#f43f5e" }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            {/* Course Header */}
            <div className="bg-white rounded-lg shadow-md border-t-4 border-t-blue-500">
                <div className="p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold">Course Details</h1>
                            <p className="text-gray-600">Course ID: {courseid}</p>
                        </div>
                        <div className="text-right">
                            <div className="flex items-center gap-2">
                                <GraduationCap className="w-5 h-5" />
                                <span>Overall Progress: {overallProgress}%</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Performance Section */}
            <div className="grid md:grid-cols-3 gap-6">
                {/* Quizzes Card */}
                <div className="bg-white rounded-lg shadow-md">
                    <div className="p-6">
                        <h2 className="text-xl font-semibold flex items-center gap-2 mb-6">
                            <Award className="w-5 h-5" />
                            Performance Scores
                        </h2>
                        <PerformanceCard
                            type="quizzes"
                            icon={Award}
                            color="blue"
                        />
                    </div>
                </div>

                {/* Assignments Card */}
                <div className="bg-white rounded-lg shadow-md">
                    <div className="p-6">
                        <h2 className="text-xl font-semibold flex items-center gap-2 mb-6">
                            <FileText className="w-5 h-5" />
                            Assignment Performance
                        </h2>
                        <PerformanceCard
                            type="assignments"
                            icon={FileText}
                            color="green"
                        />
                    </div>
                </div>

                {/* Exams Card */}
                <div className="bg-white rounded-lg shadow-md">
                    <div className="p-6">
                        <h2 className="text-xl font-semibold flex items-center gap-2 mb-6">
                            <ClipboardList className="w-5 h-5" />
                            Exam Performance
                        </h2>
                        <PerformanceCard
                            type="exams"
                            icon={ClipboardList}
                            color="rose"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CoursePage;